import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './context/AuthContext.jsx';
import AppShell from './components/AppShell.jsx';
import LoginPage from './pages/LoginPage.jsx';
import QueuePage from './pages/QueuePage.jsx';
import TodayPage from './pages/TodayPage.jsx';
import MyQuotationsPage from './pages/MyQuotationsPage.jsx';
import SalesQuotationDetailPage from './pages/SalesQuotationDetailPage.jsx';
import WorkspacePage from './pages/WorkspacePage.jsx';
import ApprovalQueuePage from './pages/ApprovalQueuePage.jsx';
import ApprovalDetailPage from './pages/ApprovalDetailPage.jsx';
import AllRequestsPage from './pages/AllRequestsPage.jsx';
import AdminRequestDetailPage from './pages/AdminRequestDetailPage.jsx';
import AllProformasPage from './pages/AllProformasPage.jsx';
import AllProformasViewPage from './pages/AllProformasViewPage.jsx';
import CatalogPage from './pages/CatalogPage.jsx';
import CostManagementPage from './pages/CostManagementPage.jsx';
import UsersPage from './pages/UsersPage.jsx';
import CustomersPage from './pages/CustomersPage.jsx';
import CustomerDetailPage from './pages/CustomerDetailPage.jsx';
import BankAccountsPage from './pages/BankAccountsPage.jsx';
import CategoriesPage from './pages/CategoriesPage.jsx';
import SettingsPage from './pages/SettingsPage.jsx';
import CatalogManagerRequestsPage from './pages/CatalogManagerRequestsPage.jsx';
import CatalogManagerRequestDetailPage from './pages/CatalogManagerRequestDetailPage.jsx';
import NotFoundPage from './pages/NotFoundPage.jsx';
import LoadingState from './components/LoadingState.jsx';

// Role gating is enforced by the API, not by hiding UI (admin/CLAUDE.md) —
// this guard only decides what to render (redirect to /login vs. the
// screen), it is never treated as a security boundary. Every real API call
// still goes through requireAuth/requireRole(...)/costGuard server-side
// regardless of what this component does.
function RequireRole({ roles, children }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingState variant="page" />;
  }
  if (!user || !roles.includes(user.role)) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

// "/" is role-based routing, not one screen: a sales session reaches their
// pricing queue, an admin session reaches the approval queue — this task's
// explicit requirement ("an admin landing on / reaches the approval queue,
// not the sales queue"). Kept as its own small component rather than
// inlining a ternary in the route table below, so the two real screens
// (QueuePage/ApprovalQueuePage) stay separate components per role, not one
// component with a conditional (admin/CLAUDE.md).
//
// catalog_manager (migration 025, third staff role): catalog work is this
// role's primary job, and CatalogPage already has its own canonical URL
// (/products, CatalogManagerNav's own first link) — a REDIRECT there
// instead of rendering <CatalogPage /> inline at "/" too, so there is never
// a second URL for the same screen and the nav item's `end` match still
// highlights correctly (rendering CatalogPage directly at both "/" and
// "/products" would leave the nav showing no active item when a
// catalog_manager session lands on "/", e.g. straight after login).
function HomePage() {
  const { user } = useAuth();
  if (user && user.role === 'admin') {
    return <ApprovalQueuePage />;
  }
  if (user && user.role === 'catalog_manager') {
    return <Navigate to="/products" replace />;
  }
  return <QueuePage />;
}

export default function App() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={
          user &&
          (user.role === 'sales' || user.role === 'admin' || user.role === 'catalog_manager') ? (
            <Navigate to="/" replace />
          ) : (
            <LoginPage />
          )
        }
      />
      <Route
        path="/"
        element={
          <RequireRole roles={['sales', 'admin', 'catalog_manager']}>
            <AppShell>
              <HomePage />
            </AppShell>
          </RequireRole>
        }
      />
      <Route
        path="/requests/:requestId"
        element={
          <RequireRole roles={['sales']}>
            <AppShell>
              <WorkspacePage />
            </AppShell>
          </RequireRole>
        }
      />
      {/* Sales "Today" nav item (SalesNav) — a daily task list, this task's
          brief. */}
      <Route
        path="/today"
        element={
          <RequireRole roles={['sales']}>
            <AppShell>
              <TodayPage />
            </AppShell>
          </RequireRole>
        }
      />
      {/* Sales "My quotations" nav item (SalesNav) — every quotation this
          salesperson has priced. */}
      <Route
        path="/my-quotations"
        element={
          <RequireRole roles={['sales']}>
            <AppShell>
              <MyQuotationsPage />
            </AppShell>
          </RequireRole>
        }
      />
      {/* Sales quotation detail — read-only document + unseal/re-quote/
          add-note actions. Not "/my-quotations/:id" colliding with any
          reserved prefix (see AdminNav.jsx's own header comment on that
          class of bug) — this path is safe. */}
      <Route
        path="/my-quotations/:id"
        element={
          <RequireRole roles={['sales']}>
            <AppShell>
              <SalesQuotationDetailPage />
            </AppShell>
          </RequireRole>
        }
      />
      <Route
        path="/quotations/:id"
        element={
          <RequireRole roles={['admin']}>
            <AppShell>
              <ApprovalDetailPage />
            </AppShell>
          </RequireRole>
        }
      />
      {/* Not "/admin/requests" — see components/AdminNav.jsx's header
          comment: that path collides with the dev proxy's (and, in
          production, Nginx's) "/admin" API prefix. */}
      <Route
        path="/all-requests"
        element={
          <RequireRole roles={['admin']}>
            <AppShell>
              <AllRequestsPage />
            </AppShell>
          </RequireRole>
        }
      />
      {/* Read-only request detail — clicking a request reference on the list
          above opens everything the request contains (header, line items,
          attachments, quotation history, notes, activity timeline). No
          actions: assignment stays on the list. Admin-only here and, the
          real gate, at GET /admin/requests/:id server-side. */}
      <Route
        path="/all-requests/:requestId"
        element={
          <RequireRole roles={['admin']}>
            <AppShell>
              <AdminRequestDetailPage />
            </AppShell>
          </RequireRole>
        }
      />
      {/* All proformas — every quotation in every state, superseded
          revisions included. Not "/admin/..." — same dev-proxy/Nginx path-
          prefix collision noted on the sibling routes; AdminNav's own link
          points here. Admin-only both here (RequireRole, UX) and, the real
          gate, at GET /admin/quotations/all-proformas server-side. */}
      <Route
        path="/all-proformas"
        element={
          <RequireRole roles={['admin']}>
            <AppShell>
              <AllProformasPage />
            </AppShell>
          </RequireRole>
        }
      />
      {/* Read-only proforma viewer — clicking a proforma number on the list
          above opens the rendered document (the same shared component the
          customer's /proforma page uses) with a Download PDF link. */}
      <Route
        path="/all-proformas/:id"
        element={
          <RequireRole roles={['admin']}>
            <AppShell>
              <AllProformasViewPage />
            </AppShell>
          </RequireRole>
        }
      />
      {/* Not "/catalog" or "/admin/catalog" — both collide with the dev
          proxy's/Nginx's API path prefixes, see
          components/AdminNav.jsx's own header comment and this task's
          brief; AdminNav's own "Catalog" link already points at exactly
          this path. 'catalog_manager' (migration 025) added alongside
          'admin' — same page component, same already-dual-role-reachable
          GET/POST /admin/catalog/... endpoints (see
          routes/adminCatalogRoutes.js's own comment), nothing to change in
          CatalogPage.jsx itself. */}
      <Route
        path="/products"
        element={
          <RequireRole roles={['admin', 'catalog_manager']}>
            <AppShell>
              <CatalogPage />
            </AppShell>
          </RequireRole>
        }
      />
      {/* Admin console screen 3 of 5. Not "/cost" or "/catalog/cost" or
          anything starting with "/admin" — same dev-proxy/Nginx path-prefix
          collision noted on /products and /all-requests above, and in this
          task's own brief; AdminNav's own "Cost management" link already
          points at exactly this path. 'catalog_manager' (migration 025)
          added alongside 'admin' — this role owns cost management
          end-to-end (routes/adminCatalogRoutes.js's own comment: both roles
          are allowed to see cost data); sales still never reaches this
          component at all (see pages/CostManagementPage.jsx's own header
          comment). */}
      <Route
        path="/cost-management"
        element={
          <RequireRole roles={['admin', 'catalog_manager']}>
            <AppShell>
              <CostManagementPage />
            </AppShell>
          </RequireRole>
        }
      />
      {/* Admin console screen 4 of 5. Not "/admin/users" — same dev-proxy/
          Nginx path-prefix collision noted on /products, /cost-management,
          and /all-requests above, and in this task's own brief; AdminNav's
          own "Users" link already points at exactly this path. Create and
          deactivate staff accounts (sales or, as of migration 025,
          catalog_manager too) — see pages/UsersPage.jsx's own header
          comment for the exact scope. Admin-only route: catalog_manager
          accounts are managed BY an admin here, this role never reaches
          this screen itself. */}
      <Route
        path="/users"
        element={
          <RequireRole roles={['admin']}>
            <AppShell>
              <UsersPage />
            </AppShell>
          </RequireRole>
        }
      />
      {/* Customers (organizations) — customers/products/users multi-select
          bulk-actions task. Not "/admin/customers" — same dev-proxy/Nginx
          path-prefix collision noted on every other admin route above;
          AdminNav's own "Customers" link points at exactly this path. */}
      <Route
        path="/customers"
        element={
          <RequireRole roles={['admin']}>
            <AppShell>
              <CustomersPage />
            </AppShell>
          </RequireRole>
        }
      />
      <Route
        path="/customers/:id"
        element={
          <RequireRole roles={['admin']}>
            <AppShell>
              <CustomerDetailPage />
            </AppShell>
          </RequireRole>
        }
      />
      {/* Bank accounts printed on the proforma / payment-request document.
          Not "/admin/bank-accounts" — same dev-proxy/Nginx path-prefix
          collision noted on /products, /users, etc. above; AdminNav's own
          link points at exactly this path. */}
      <Route
        path="/bank-accounts"
        element={
          <RequireRole roles={['admin']}>
            <AppShell>
              <BankAccountsPage />
            </AppShell>
          </RequireRole>
        }
      />
      {/* Category tree + per-category attribute schema editor. Not
          "/admin/categories" — same path-prefix collision as the routes
          above; AdminNav's link points here. 'catalog_manager' (migration
          025) added alongside 'admin' — same page component, same
          already-dual-role-reachable /admin/catalog/categories endpoints. */}
      <Route
        path="/categories"
        element={
          <RequireRole roles={['admin', 'catalog_manager']}>
            <AppShell>
              <CategoriesPage />
            </AppShell>
          </RequireRole>
        }
      />
      {/* Admin console screen 5 of 5, the LAST screen. Not "/admin/settings"
          — same dev-proxy/Nginx path-prefix collision noted on /products,
          /cost-management, /users, and /all-requests above, and in this
          task's own brief; AdminNav's own "Settings" link already points at
          exactly this path (components/AdminNav.jsx). One global settings
          object, not a list of rows — see pages/SettingsPage.jsx's own
          header comment for the exact field list this screen shows. */}
      <Route
        path="/settings"
        element={
          <RequireRole roles={['admin']}>
            <AppShell>
              <SettingsPage />
            </AppShell>
          </RequireRole>
        }
      />
      {/* catalog_manager's own read-only "Incoming requests" screens
          (migration 025). Not "/catalog-manager/..." — that prefix is now
          proxied straight to the real backend's GET /catalog-manager/
          requests[/:id] (see admin/vite.config.js's own comment), the same
          class of collision every other admin route in this file avoids.
          catalog_manager ONLY — deliberately not admin, which keeps using
          its own richer /all-requests + /all-requests/:requestId flow (see
          components/CatalogManagerNav.jsx's own header comment and this
          task's brief: a separate, narrower surface by design, not
          something to widen admin onto). */}
      <Route
        path="/incoming-requests"
        element={
          <RequireRole roles={['catalog_manager']}>
            <AppShell>
              <CatalogManagerRequestsPage />
            </AppShell>
          </RequireRole>
        }
      />
      <Route
        path="/incoming-requests/:requestId"
        element={
          <RequireRole roles={['catalog_manager']}>
            <AppShell>
              <CatalogManagerRequestDetailPage />
            </AppShell>
          </RequireRole>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
